import { useState } from "react";
import Select from "@/components/base/Select";
import Badge from "@/components/base/Badge";
import { useProvider } from "@/hooks/useProvider";
import { teamMembers, drivers, specialties } from "@/mocks/team";
import type { TeamMember } from "@/mocks/team";

const specialtyOptions = specialties.map((s) => ({ value: s, label: s }));

const statusMap: Record<TeamMember["status"], { dot: string; label: string }> = {
  available: { dot: "bg-[#27AE60]", label: "متاح" },
  busy: { dot: "bg-[#F59E0B]", label: "مشغول بمهمة" },
  vacation: { dot: "bg-[#8AAFAF]", label: "إجازة" },
};

const specialtyColor: Record<string, string> = {
  "غسيل سيارات": "bg-[#F0FDFD] text-ghasla-text-link",
  تنظيف: "bg-[#E8F9F0] text-[#27AE60]",
  الكل: "bg-[#FFF9EB] text-[#B45309]",
};

export default function Team() {
  const { provider, loading: providerLoading } = useProvider();

  const [members, setMembers] = useState<TeamMember[]>(teamMembers);
  const [addOpen, setAddOpen] = useState(false);
  const [menuId, setMenuId] = useState<number | null>(null);
  const [newMember, setNewMember] = useState({ name: "", phone: "", specialty: specialties[0] });

  // Business logic: car wash / home cleaning providers manage their own team,
  // while laundry drivers belong to the platform (view only).
  const serviceTypes = provider?.service_types ?? [];
  // في وضع المعاينة (بدون مزوّد حقيقي) نعرض المحتوى بالكامل حتى لا تظهر الصفحة فارغة
  const hasOwnTeam = !provider || serviceTypes.some((t) => t === "car_wash" || t === "home_cleaning");
  const hasLaundry = !provider || serviceTypes.some((t) => t === "laundry");

  const addMember = () => {
    if (!newMember.name.trim()) return;
    setMembers((prev) => [
      ...prev,
      {
        id: Date.now(),
        name: newMember.name.trim(),
        phone: newMember.phone || "—",
        specialty: newMember.specialty,
        status: "available",
        tasks: 0,
        rating: "—",
        initials: newMember.name.trim().slice(0, 2),
        avatar: "",
      },
    ]);
    setNewMember({ name: "", phone: "", specialty: specialties[0] });
    setAddOpen(false);
  };

  const inputClass =
    "h-12 w-full rounded-[14px] border border-[#E4EFEF] bg-[#F0FDFD] px-4 text-sm text-ghasla-text-heading outline-none transition-colors focus:border-[#0D6E6E]";

  if (providerLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-[#E4EFEF] border-t-[#0D6E6E]" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="text-[28px] font-bold text-ghasla-text-heading">الفريق</h1>
        {hasOwnTeam && (
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            className="cursor-pointer whitespace-nowrap rounded-xl bg-gradient-to-l from-[#0D6E6E] to-[#1A9E96] px-5 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
          >
            + إضافة موظف
          </button>
        )}
      </div>

      {/* Own team cards — car wash / home cleaning providers only */}
      {hasOwnTeam && (
        <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
          {members.map((m) => (
            <div key={m.id} className="relative rounded-[20px] bg-white p-6 shadow-card">
              <button
                type="button"
                onClick={() => setMenuId(menuId === m.id ? null : m.id)}
                className="absolute left-4 top-4 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-ghasla-text-secondary hover:bg-[#F0FDFD]"
                aria-label="قائمة"
              >
                <i className="ri-more-2-fill" />
              </button>

              {menuId === m.id && (
                <div className="absolute left-4 top-14 z-10 w-28 overflow-hidden rounded-xl bg-white shadow-card">
                  {["تعديل", "إيقاف", "حذف"].map((action, i) => (
                    <button
                      key={action}
                      type="button"
                      onClick={() => setMenuId(null)}
                      className={`block w-full cursor-pointer whitespace-nowrap px-4 py-2.5 text-right text-sm transition-colors hover:bg-[#F0FDFD] ${
                        i === 2 ? "text-[#E74C3C]" : "text-ghasla-text-primary"
                      }`}
                    >
                      {action}
                    </button>
                  ))}
                </div>
              )}

              <div className="flex items-center gap-3">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#F0FDFD] text-lg font-semibold text-ghasla-text-link">
                  {m.avatar ? (
                    <img src={m.avatar} alt={m.name} className="h-full w-full object-cover" />
                  ) : (
                    m.initials
                  )}
                </div>
                <div>
                  <p className="text-[17px] font-semibold text-ghasla-text-heading">{m.name}</p>
                  <p className="text-sm text-ghasla-text-primary" dir="ltr">{m.phone}</p>
                </div>
              </div>

              <div className="mt-4 flex items-center gap-2">
                <span className={`rounded-full px-3 py-1 text-xs font-medium ${specialtyColor[m.specialty] ?? "bg-[#F0FDFD] text-ghasla-text-link"}`}>
                  {m.specialty}
                </span>
                <span className="flex items-center gap-1.5 text-xs text-ghasla-text-primary">
                  <span className={`h-2 w-2 rounded-full ${statusMap[m.status].dot}`} />
                  {statusMap[m.status].label}
                </span>
              </div>

              <div className="mt-4 flex items-center gap-5 border-t border-[#F4F8F8] pt-4 text-sm">
                <span className="text-ghasla-text-primary">{m.tasks} مهمة</span>
                <span className="flex items-center gap-1 text-ghasla-text-primary">
                  <i className="ri-star-fill text-[#F59E0B]" />
                  {m.rating}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Platform drivers — laundry providers only (view only) */}
      {hasLaundry && (
        <div className={hasOwnTeam ? "mt-8" : "mt-6"}>
          <h2 className="text-xl font-semibold text-ghasla-text-heading">مناديب التوصيل</h2>
          <p className="mt-1 text-sm text-ghasla-text-primary">المناديب تابعين لمنصة غسلة — تقدر تشوف المندوب المعيّن لكل طلب</p>

          <div className="mt-4 flex items-start gap-3 rounded-2xl bg-[#F0FDFD] p-4">
            <i className="ri-information-line mt-0.5 text-lg text-ghasla-text-link" />
            <p className="text-sm text-ghasla-text-primary">
              مناديب المغاسل يتم تعيينهم تلقائياً من المنصة. لو عندك ملاحظة على مندوب تواصل مع الدعم.
            </p>
          </div>

          <div className="mt-4 overflow-hidden rounded-[20px] bg-white shadow-card">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[520px] text-sm">
                <thead>
                  <tr className="bg-[#F0FDFD] text-xs font-semibold text-ghasla-text-secondary">
                    <th className="h-12 px-5 text-right">المندوب</th>
                    <th className="h-12 px-5 text-right">الطلب</th>
                    <th className="h-12 px-5 text-right">الحالة</th>
                    <th className="h-12 px-5 text-right">التقييم</th>
                  </tr>
                </thead>
                <tbody>
                  {drivers.map((d) => (
                    <tr key={d.id} className="border-b border-[#F4F8F8] last:border-0 hover:bg-[#FAFEFE]">
                      <td className="h-14 px-5 font-medium text-ghasla-text-heading">{d.name}</td>
                      <td className="h-14 px-5 text-ghasla-text-link">{d.order}</td>
                      <td className="h-14 px-5">
                        <Badge tone={d.status === "تم التسليم" ? "green" : "blue"}>{d.status}</Badge>
                      </td>
                      <td className="h-14 px-5">
                        <span className="flex items-center gap-1 text-ghasla-text-primary">
                          <i className="ri-star-fill text-[#F59E0B]" />
                          {d.rating}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Add member inline form */}
      {hasOwnTeam && addOpen && (
        <div className="mt-5 rounded-[20px] border border-[#E4EFEF] bg-[#F0FDFD] p-5 md:p-6">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold text-ghasla-text-heading">إضافة موظف جديد</h3>
            <button
              type="button"
              onClick={() => setAddOpen(false)}
              className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-ghasla-text-secondary hover:bg-[#E4EFEF]"
            >
              <i className="ri-close-line" />
            </button>
          </div>
          <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className="mb-2 block text-sm font-medium text-ghasla-text-primary">الاسم الكامل</label>
              <input
                className={inputClass}
                value={newMember.name}
                onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-ghasla-text-primary">رقم الجوال</label>
              <input
                className={inputClass}
                dir="ltr"
                value={newMember.phone}
                onChange={(e) => setNewMember({ ...newMember, phone: e.target.value })}
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-ghasla-text-primary">التخصص</label>
              <Select
                value={newMember.specialty}
                onChange={(v) => setNewMember({ ...newMember, specialty: v })}
                options={specialtyOptions}
              />
            </div>
          </div>
          <div className="mt-4 flex gap-3">
            <button
              type="button"
              onClick={addMember}
              className="flex-1 cursor-pointer whitespace-nowrap rounded-xl bg-gradient-to-l from-[#0D6E6E] to-[#1A9E96] px-4 py-2.5 text-sm font-semibold text-white hover:opacity-90"
            >
              حفظ الموظف
            </button>
            <button
              type="button"
              onClick={() => setAddOpen(false)}
              className="cursor-pointer whitespace-nowrap rounded-xl border border-[#E4EFEF] bg-white px-5 py-2.5 text-sm font-medium text-ghasla-text-primary hover:bg-[#F0FDFD]"
            >
              إلغاء
            </button>
          </div>
        </div>
      )}
    </div>
  );
}